import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSWRConfig } from 'swr'
import { match } from 'ts-pattern'

import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { Button } from '@frame/ui-primitives/button'

import { TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { ItemKind } from '@frame/lib/hooks/useGetItems'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useVerifyViaDocuments } from '@frame/lib/hooks/useVerifyViaDocuments'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

type Props = {
  kind: ItemKind
}

export function SearchFooter({ kind }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const task = useDirectTask()
  const { actions } = useRootContext()
  const { mutateAndNavigate } = useAutoNavigate()
  const { documentsEnabled } = useStrategies()
  const verifyViaDocuments = useVerifyViaDocuments()
  const [isLoading, setIsLoading] = useState(false)
  const { mutate } = useSWRConfig()

  const params = new URLSearchParams(location.search)

  const handleClickCannotFindProvider = async (employerName: string | null) => {
    if (documentsEnabled) {
      setIsLoading(true)
      verifyViaDocuments(task)
    } else if (task?.is_external_employment) {
      await actions.skipDirectTask(task.id)
      mutateAndNavigate()
    } else {
      const confirmEmployerPath = task?.id ? `${routes.confirmEmployer}/${task.id}` : routes.confirmEmployer
      navigate(`${confirmEmployerPath}?q=${employerName}`)
    }
  }

  return match(kind)
    .with(ItemKind.PLATFORM, ItemKind.BENEFITS, () => (
      <div data-cy={DataAttribute.UNIFIED_SEARCH_FOOTER} className="mt-3 flex flex-col space-y-5 p-5">
        <Button
          intent="secondary"
          disabled={isLoading}
          Icon={isLoading ? LoadingSpinner : undefined}
          onClick={() => handleClickCannotFindProvider(params.get('employerName'))}
        >
          I can't find the payroll provider
        </Button>
      </div>
    ))
    .with(ItemKind.EMPLOYER, ItemKind.GIG, () => (
      <div data-cy={DataAttribute.UNIFIED_SEARCH_FOOTER} className="mt-3 flex flex-col space-y-5 p-5">
        <Button
          intent="secondary"
          onClick={() => {
            const employerNameParam = new URLSearchParams()
            employerNameParam.set('employerName', params.get('q') || '')
            if (task?.id) {
              navigate(`${routes.platformCheck}/${task.id}?${employerNameParam.toString()}`)
            } else {
              navigate(`${routes.platformCheck}?${employerNameParam.toString()}`)
            }
          }}
        >
          I can't find my employer
        </Button>
      </div>
    ))
    .with(ItemKind.INCOME, () => (
      <div data-cy={DataAttribute.UNIFIED_SEARCH_FOOTER} className="mt-3 flex flex-col space-y-5 p-5">
        <Button
          intent="secondary"
          onClick={async () => {
            if (task?.strategy_info?.show_employee_company_match_picker) {
              const preparedTask = await actions.addStrategyToDirectTask({
                strategy: TrueworkJsStrategy.CREDENTIALS,
                directTaskId: task.id,
              })
              await mutate(['all', 'tasks'])
              if (!preparedTask) {
                throw new Error('Failed to add credentials strategy to task')
              }
            }
            navigate(routes.root)
          }}
        >
          None of these
        </Button>
      </div>
    ))
    .exhaustive()
}
